import { registerPlugin } from "@capacitor/core";
import { isNativeShell } from "./shell";

/**
 * Local notifications behind `Platform.supportsLocalNotifications` (FINISH_PLAN WP4.3, X9): "your upgrade is done",
 * "the Tribute is full", "new Daily Orders". Local only: scheduled on the device as the app goes to the background and
 * cleared when it comes back, so nothing needs a server. Off until the player turns them on (Settings → Reminders),
 * which is when iOS asks for permission. The web build has none (a web push needs a server).
 */

/** One notification to show at `atSeconds` (UTC seconds). `id` is stable per thing, so a re-plan replaces it. */
export interface Reminder {
  readonly id: number;
  readonly atSeconds: number;
  readonly title: string;
  readonly body: string;
}

/** Reminder id blocks: one per kind; an upgrade's is `Upgrade + job id`. */
export const ReminderIds = { orders: 1, tribute: 2, upgrade: 1000 } as const;

/** A reminder sooner than this is not worth it: the player is still holding the phone. */
export const REMINDER_MIN_LEAD_SECONDS = 60;
/** Local hours a reminder may arrive in: one that falls outside moves to the next `QuietHours.end`. */
export const QuietHours = { start: 21, end: 9 } as const;

/** What the plan is made from: the running upgrades, the orders reset and any feature's own (the Tribute, WP1). */
export interface ReminderInput {
  readonly now: number;
  readonly upgrades: ReadonlyArray<{ readonly id: number; readonly endAt: number; readonly name: string }>;
  /** Next Daily Orders reset (UTC seconds), or null when there is nothing new to say. */
  readonly ordersResetAt: number | null;
  readonly extra?: readonly Reminder[];
}

/** Moves a time out of the local quiet hours (the morning after, at `QuietHours.end`). */
export function outsideQuietHours(atSeconds: number, tzOffsetMinutes: number): number {
  const SecondsPerHour = 3600, SecondsPerMinute = 60, HoursPerDay = 24;
  const local = atSeconds - tzOffsetMinutes * SecondsPerMinute;
  const hour = Math.floor(local / SecondsPerHour) % HoursPerDay;
  if (hour >= QuietHours.end && hour < QuietHours.start) return atSeconds;
  const dayStart = local - (local % (HoursPerDay * SecondsPerHour));
  const morning = dayStart + QuietHours.end * SecondsPerHour + (hour >= QuietHours.start ? HoursPerDay * SecondsPerHour : 0);
  return morning + tzOffsetMinutes * SecondsPerMinute;
}

/**
 * The reminders to schedule: one per running upgrade and the orders reset, each out of the quiet hours and at least
 * `REMINDER_MIN_LEAD_SECONDS` away. `tzOffsetMinutes` is `Date#getTimezoneOffset()` (minutes behind UTC).
 */
export function planReminders(input: ReminderInput, tzOffsetMinutes: number): Reminder[] {
  const out: Reminder[] = [];
  const add = (r: Reminder) => {
    const at = outsideQuietHours(r.atSeconds, tzOffsetMinutes);
    if (at - input.now >= REMINDER_MIN_LEAD_SECONDS) out.push({ ...r, atSeconds: at });
  };
  for (const j of input.upgrades) add({ id: ReminderIds.upgrade + j.id, atSeconds: j.endAt, title: "Upgrade finished", body: `${j.name} is ready. A builder is free for the next job.` });
  if (input.ordersResetAt !== null) add({ id: ReminderIds.orders, atSeconds: input.ordersResetAt, title: "New Daily Orders", body: "Three new orders are waiting at the fortress." });
  for (const r of input.extra ?? []) add(r);
  return out.sort((a, b) => a.atSeconds - b.atSeconds);
}

interface LocalNotificationsPlugin {
  checkPermissions(): Promise<{ display: string }>;
  requestPermissions(): Promise<{ display: string }>;
  schedule(o: { notifications: Array<{ id: number; title: string; body: string; schedule: { at: Date; allowWhileIdle: boolean } }> }): Promise<unknown>;
  getPending(): Promise<{ notifications: Array<{ id: number }> }>;
  cancel(o: { notifications: Array<{ id: number }> }): Promise<void>;
}

/** The device side: schedules a plan in place of the last one. A no-op where local notifications do not exist. */
export class LocalReminders {
  /** Per-device opt-in (not game state: a restored save must not switch notifications on). */
  static readonly OptInKey = "highmarch.reminders";
  private readonly plugin = registerPlugin<LocalNotificationsPlugin>("LocalNotifications");

  constructor(readonly available: boolean = isNativeShell()) {}

  get enabled(): boolean {
    try { return this.available && globalThis.localStorage?.getItem(LocalReminders.OptInKey) === "on"; } catch { return false; }
  }

  /** Settings → Reminders: turning on asks iOS for permission; resolves whether reminders are on afterwards. */
  async setEnabled(on: boolean): Promise<boolean> {
    if (!this.available) return false;
    let granted = false;
    if (on) {
      try { granted = (await this.plugin.requestPermissions()).display === "granted"; } catch { granted = false; }
    }
    try { globalThis.localStorage?.setItem(LocalReminders.OptInKey, granted ? "on" : "off"); } catch { /* private mode */ }
    if (!granted) await this.clear();
    return granted;
  }

  /** Replaces whatever this game scheduled with `plan`. */
  async schedule(plan: readonly Reminder[]): Promise<void> {
    if (!this.enabled) return;
    await this.clear();
    if (plan.length === 0) return;
    const MsPerSecond = 1000;
    await this.plugin.schedule({ notifications: plan.map((r) => ({ id: r.id, title: r.title, body: r.body, schedule: { at: new Date(r.atSeconds * MsPerSecond), allowWhileIdle: true } })) }).catch(() => undefined);
  }

  /** The player is back: nothing needs telling. */
  async clear(): Promise<void> {
    if (!this.available) return;
    try {
      const pending = (await this.plugin.getPending()).notifications;
      if (pending.length > 0) await this.plugin.cancel({ notifications: pending.map((n) => ({ id: n.id })) });
    } catch { /* permission never granted: nothing pending */ }
  }
}

/** Reminders other features add (the Tribute's "full" time, WP1): called with the clock when the plan is made. */
const sources: Array<(now: number) => readonly Reminder[]> = [];
export function registerReminderSource(source: (now: number) => readonly Reminder[]): () => void {
  sources.push(source);
  return () => { const i = sources.indexOf(source); if (i >= 0) sources.splice(i, 1); };
}
export function extraReminders(now: number): Reminder[] { return sources.flatMap((s) => s(now)); }

export const reminders = new LocalReminders();
